import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, map, of, switchMap, withLatestFrom } from 'rxjs';
import { PRODUCT_SORT } from '@components/shop/products/list/list.config';
import {
  PurchaseProductQuerySortBy,
  PurchaseProductShopType,
  SortDirection
} from '@interfaces/schema';
import { shopCatalogActions } from './catalog.actions';
import {
  selectShopCatalogDNAProductsQuery,
  selectShopCatalogProductGroupDNAQuery
} from './catalog.selectors';
import { ShopCatalogService } from './catalog.service';

/**
 * Store effects of shop catalog page.
 * This class provides side effect action to load data from server.
 */
@Injectable()
export class ShopCatalogEffects extends ShopCatalogService {
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);

  readonly fetchDNAProducts = createEffect(() => {
    return this.actions$.pipe(
      ofType(shopCatalogActions.fetchDNAProducts),
      switchMap((action) =>
        of(action).pipe(
          withLatestFrom(
            this.store.select(selectShopCatalogProductGroupDNAQuery),
            this.store.select(selectShopCatalogDNAProductsQuery)
          )
        )
      ),
      switchMap(([params, productGroupDNAQuery, dnaProductsQuery]) =>
        of(true).pipe(
          switchMap(() => {
            return productGroupDNAQuery.result
              ? of(productGroupDNAQuery.result)
              : this.fetchProductGroups({
                  where: {
                    isDNA: true
                  },
                  limit: 1
                }).pipe(
                  map((response) =>
                    Array.isArray(response.results) &&
                    response.results.length > 0
                      ? response.results[0]
                      : undefined
                  )
                );
          }),
          switchMap((productGroupDNA) => {
            if (productGroupDNA?.id) {
              return this.fetchProducts({
                where: {
                  productGroupId: [productGroupDNA.id],
                  shopTypes: [PurchaseProductShopType.Regular],
                  type: PurchaseProductShopType.Regular
                },
                limit: dnaProductsQuery.limit,
                sort: PRODUCT_SORT as PurchaseProductQuerySortBy,
                offset:
                  (params.page > 0 ? params.page - 1 : params.page) *
                  dnaProductsQuery.limit
              }).pipe(
                switchMap((response) => {
                  return of(
                    shopCatalogActions.fetchProductGroupDNASuccess({
                      result: productGroupDNA
                    }),
                    shopCatalogActions.fetchDNAProductsSuccess({
                      items: response?.results ?? [],
                      page: params.page,
                      totalItems: response?.count ?? 0
                    })
                  );
                })
              );
            }
            return of(
              shopCatalogActions.fetchProductGroupDNASuccess({
                result: productGroupDNA
              }),
              shopCatalogActions.fetchDNAProductsSuccess({
                items: [],
                page: params.page,
                totalItems: 0
              })
            );
          }),
          catchError((err) => {
            return of(
              shopCatalogActions.fetchProductGroupDNAFailed({
                error: { message: err.message }
              }),
              shopCatalogActions.fetchDNAProductsFailed({
                error: {
                  message: err.message
                }
              })
            );
          })
        )
      )
    );
  });

  readonly fetchProductGroupDNA$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(shopCatalogActions.fetchProductGroupDNA),
      switchMap(() =>
        this.fetchProductGroups({
          where: {
            isDNA: true
          },
          limit: 1
        }).pipe(
          switchMap((response) => {
            return of(
              shopCatalogActions.fetchProductGroupDNASuccess({
                result:
                  Array.isArray(response.results) && response.results.length > 0
                    ? response.results[0]
                    : undefined
              })
            );
          }),
          catchError((err) => {
            return of(
              shopCatalogActions.fetchProductGroupDNAFailed({
                error: {
                  message: err?.message
                }
              })
            );
          })
        )
      )
    );
  });

  readonly fetchProductGroups$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(shopCatalogActions.fetchProductGroups),
      switchMap((payload) =>
        this.fetchProductGroups({
          where: {
            isVisible: true,
            isDNA: false
          },
          limit: 12,
          offset: (payload.page > 0 ? payload.page - 1 : payload.page) * 12,
          sort: {
            position: SortDirection.Asc
          }
        }).pipe(
          switchMap((response) => {
            if (payload.reset) {
              return of(
                shopCatalogActions.resetProductGroups(),
                shopCatalogActions.fetchProductGroupsSuccess({
                  items: response.results ?? [],
                  page: payload.page,
                  totalItems: response.count ?? 0
                })
              );
            }
            return of(
              shopCatalogActions.fetchProductGroupsSuccess({
                items: response.results ?? [],
                page: payload.page,
                totalItems: response.count ?? 0
              })
            );
          }),
          catchError((err) => {
            if (payload.reset) {
              return of(
                shopCatalogActions.resetProductGroups(),
                shopCatalogActions.fetchProductGroupsFailed({
                  error: {
                    message: err?.message
                  }
                })
              );
            }
            return of(
              shopCatalogActions.fetchProductGroupsFailed({
                error: {
                  message: err?.message
                }
              })
            );
          })
        )
      )
    );
  });
}
